/** A box in viewport pixels — what `getBoundingClientRect` gives, without the methods. */
export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface SpotlightPlacement {
  /** The lit hole around the target, padding included. Absent when there is no target. */
  hole?: Box;
  /** Where the popover's top-left corner goes, in viewport pixels. */
  top: number;
  left: number;
  /** Which side of the hole the popover sits on; `center` when there is no target to point at. */
  side: 'bottom' | 'top' | 'center';
  /** The arrow's centre, measured from the popover's left edge. */
  arrowX: number;
}

export interface SpotlightPlacementInput {
  /** The highlighted element's box, or undefined to centre the popover with no hole. */
  target: Box | undefined;
  popover: { width: number; height: number };
  viewport: { width: number; height: number };
  /** Space between the target and the edge of the hole. */
  padding: number;
  /** Space between the hole and the popover, which the arrow bridges. */
  gap: number;
  /** The closest the popover may come to a viewport edge — the page's side gutter. */
  margin: number;
}

/** Keeps the arrow off the popover's rounded corners. */
const ARROW_INSET = 20;

const clamp = (value: number, min: number, max: number) =>
  // When the range is inverted (a popover wider than the room), the low end wins: start at the edge.
  Math.max(min, Math.min(value, max));

/**
 * Where a spotlight's popover goes: below the hole when it fits, above when only that fits, and
 * otherwise on whichever side has more room, clamped on screen — overlapping the hole is better
 * than a popover with its buttons off the bottom of a phone.
 *
 * Horizontally it centres on the target, held `margin` from both viewport edges, and the arrow
 * slides along the popover's edge so it still points at the target's middle.
 *
 * Pure geometry, kept apart from the component so the rules read in one place.
 */
export function placeSpotlight({
  target,
  popover,
  viewport,
  padding,
  gap,
  margin,
}: SpotlightPlacementInput): SpotlightPlacement {
  const maxLeft = viewport.width - margin - popover.width;
  const maxTop = viewport.height - margin - popover.height;

  if (!target) {
    return {
      top: clamp((viewport.height - popover.height) / 2, margin, maxTop),
      left: clamp((viewport.width - popover.width) / 2, margin, maxLeft),
      side: 'center',
      arrowX: popover.width / 2,
    };
  }

  const hole: Box = {
    top: target.top - padding,
    left: target.left - padding,
    width: target.width + padding * 2,
    height: target.height + padding * 2,
  };
  const holeBottom = hole.top + hole.height;

  const roomBelow = viewport.height - margin - (holeBottom + gap);
  const roomAbove = hole.top - gap - margin;
  const side: 'bottom' | 'top' =
    roomBelow >= popover.height || roomBelow >= roomAbove ? 'bottom' : 'top';

  const top = clamp(
    side === 'bottom' ? holeBottom + gap : hole.top - gap - popover.height,
    margin,
    maxTop,
  );

  const centre = target.left + target.width / 2;
  const left = clamp(centre - popover.width / 2, margin, maxLeft);
  const arrowX = clamp(centre - left, ARROW_INSET, popover.width - ARROW_INSET);

  return { hole, top, left, side, arrowX };
}
